import { addMethod, string } from 'yup';
import { addressSchema, buildMetadata, getRequiredMessageDescriptorForField } from '../../../address/address-fields/address-schema';
import { commonMessages } from '../../../address/address-fields/messages';
import { FIELD_NAMES } from '../../../address/address-fields/constants';
import { buildMessageCallback } from '../../../validation/utilities';
import { messages } from '../../modals/wallet-registration/components/billing-and-pickup/message';
import { US } from '../../../constants/country-codes';

const { EMAIL, COUNTRY_CODE, PHONE, COMPANY, NAME } = FIELD_NAMES;

const getLocalizedRequiredMessage = (countryCode, fieldSchema) => {
  const fieldSchemaDescription = fieldSchema.describe();
  const fieldName = fieldSchemaDescription.meta.fieldName;

  const messageDescriptor = getRequiredMessageDescriptorForField(countryCode, fieldName);
  return fieldSchema.required(buildMessageCallback(messageDescriptor));
};

const isValidFullName = 'isValidFullName';
const IS_VALID_FULL_NAME = 'is-valid-full-name';
const isValidPhoneNumber = 'isValidPhoneNumber';
const IS_VALID_PHONE_NUMBER = 'is-valid-phone-number';
const isValidCompany = 'isValidCompany';
const IS_VALID_COMPANY = 'is-valid-company';

addMethod(string, isValidFullName, function (messageCallback) {
  return this.test(IS_VALID_FULL_NAME, messageCallback, (value) => {
    if (!value) {
      return false;
    }
    const names = value.trim().split(' ');
    const firstName = names[0];
    const lastName = names.slice(1).join(' ');
    return firstName.length >= 2 && lastName.length >= 2;
  });
});

addMethod(string, isValidPhoneNumber, function (messageCallback) {
  return this.test(IS_VALID_PHONE_NUMBER, messageCallback, (value) => {
    if (!value) {
      return false;
    }
    const digitsOnlyNumber = value.replace(/\D+/g, '');
    return digitsOnlyNumber && digitsOnlyNumber.length >= 10 && digitsOnlyNumber.length <= 15;
  });
});

addMethod(string, isValidCompany, function (messageCallback) {
  return this.test(IS_VALID_COMPANY, messageCallback, (value) => {
    if (!value) {
      return false;
    }
    return value.length >= 2 && value.length <= 50;
  });
});

export const WalletAddressSchema = addressSchema.shape({
  [EMAIL]: string()
    .email(buildMessageCallback(commonMessages.emailFormatValidationError))
    .meta(buildMetadata(EMAIL))
    .when(COUNTRY_CODE, getLocalizedRequiredMessage),
  [NAME]: addressSchema.fields[NAME].when(COUNTRY_CODE, {
    is: US,
    then: (schema) => schema.isValidFullName(buildMessageCallback(messages.fullNameValidationError)),
    otherwise: (schema) => schema,
  }),
  [PHONE]: addressSchema.fields[PHONE]
    .when(COUNTRY_CODE, {
      is: US,
      then: (schema) => schema.isValidPhoneNumber(buildMessageCallback(messages.phoneNumberValidationError)),
      otherwise: (schema) => schema,
    })
    .test({
      name: 'phone number contains invalid characters',
      message: buildMessageCallback(messages.phoneNumberValidationCharError),
      test: (value) => !value || /^[\d()+\s-]+$/.test(value),
    }),
  [COMPANY]: addressSchema.fields[COMPANY].when(COUNTRY_CODE, {
    is: US,
    then: (schema) => schema.isValidCompany(buildMessageCallback(messages.companyLengthValidationError)),
    otherwise: (schema) => schema,
  }),
});
